import FuseAnimate from '@/@fuse/core/FuseAnimate';
import { getRoomRatioSummary, getRooms } from '@/app/services/rooms/rooms';
import * as Actions from '@/app/store/actions';
import { Avatar, CircularProgress, IconButton, InputAdornment, MenuItem, Popover, TextField } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { ArrowForwardIos } from '@material-ui/icons';
import Pusher from 'pusher-js';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import AllRoomsRatioStats from './RatioStats/AllRoomsRatioStats';
import { calculateRatio } from './RatioStats/helpers';
import './roomspage.css';
import GradientButton from '@shared/components/GradientButton';
import UploadIcon from '@shared/icons/UploadIcon';
import { useNavigate } from 'react-router-dom';

const AllRooms = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [searchQuery, setSearchQuery] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [rooms, setRooms] = useState([]);
	const [firstLoad, setFirstLoad] = useState(true);
	const [fetchingMore, setFetchingMore] = useState(false);
	const [hasMore, setHasMore] = useState(false);
	const [page, setPage] = useState(1);
	const [ratioData, setRatioData] = useState(null);
	const [ratiosLoading, setRatiosLoading] = useState(false);
	const user = useSelector(({ auth }) => auth.user);
	const [anchorEl, setAnchorEl] = useState(null);

	// Listen for ratio changes and update components
	useEffect(() => {
		let isMounted = true;

		const pusher = new Pusher(import.meta.env.VITE_PUSHER_CHANNEL_ID, {
			cluster: import.meta.env.VITE_PUSHER_CLUSTER_ID,
			forceTLS: true,
		});
		const channel = pusher.subscribe(
			`ld-channel-${
				user.role[0] === 'school_admin' || user.role[0] === 'sub_admin' ? user.data.school.id : user.school?.id
			}`
		);

		channel.bind('ld-room-ratio-update-event', () => {
			if (isMounted) {
				setRatiosLoading(true);
				getRoomRatioSummary().then((res) => {
					if (isMounted) {
						setRatioData(res.data);
						setRatiosLoading(false);
					}
				});

				setIsLoading(true);
				getRooms(searchQuery, 1, '')
					.then((res) => {
						if (isMounted) {
							if (res.data.meta.last_page > res.data.meta.current_page) {
								setHasMore(true);
							} else {
								setHasMore(false);
							}
							setPage(res.data.meta.current_page + 1);
							setRooms(res.data.data);
						}
					})
					.catch(() => {
						if (isMounted) {
							dispatch(
								Actions.showMessage({
									message: 'Failed to fetch updated room data',
									variant: 'error',
								})
							);
						}
					})
					.finally(() => {
						if (isMounted) {
							setIsLoading(false);
						}
					});
			}
		});

		return () => {
			isMounted = false;
			pusher.disconnect();
		};
	}, [user]);

	const handleSearch = (e) => {
		setSearchQuery(e.target.value);
		setPage(1);
	};

	const handleGoToRoom = (room) => {
		navigate(`/rooms-room/${room.id}`);
	};

	useEffect(() => {
		setRatiosLoading(true);
		getRoomRatioSummary().then((res) => {
			setRatioData(res.data);
			setRatiosLoading(false);
		});
	}, []);

	const handleLoadMore = () => {
		setFetchingMore(true);
		getRooms(searchQuery, page)
			.then((res) => {
				if (res.data.meta.last_page > res.data.meta.current_page) {
					setHasMore(true);
				} else {
					setHasMore(false);
				}
				setPage(res.data.meta.current_page + 1);
				setRooms(rooms.concat(res.data.data));
				setFetchingMore(false);
			})
			.catch(() => {
				setFetchingMore(false);
				dispatch(
					Actions.showMessage({
						message: 'Failed to fetch data, please refresh',
						variant: 'error',
					})
				);
			});
	};

	useEffect(() => {
		let isMounted = true;
		let debounceTimer;

		const fetchRooms = () => {
			if (!isMounted) return;
			setIsLoading(true);
			setFirstLoad(false);
			getRooms(searchQuery, 1)
				.then((res) => {
					if (!isMounted) return;
					if (res.data.meta.last_page > res.data.meta.current_page) {
						setHasMore(true);
					} else {
						setHasMore(false);
					}
					setPage(res.data.meta.current_page + 1);
					setRooms(res.data.data);
				})
				.catch(() => {
					if (!isMounted) return;
					dispatch(
						Actions.showMessage({
							message: 'Failed to fetch data, please refresh',
							variant: 'error',
						})
					);
				})
				.finally(() => {
					if (isMounted) {
						setIsLoading(false);
					}
				});
		};
		clearTimeout(debounceTimer);

		debounceTimer = setTimeout(fetchRooms, firstLoad ? 0 : 300);

		return () => {
			isMounted = false;
			clearTimeout(debounceTimer);
		};
	}, [searchQuery]);

	const handleGoToUploadSchedules = () => {
		navigate('/rooms-upload-schedules');
	};

	const handleOpenAddRoomPopover = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	return (
		<FuseAnimate animation="transition.slideLeftIn" duration={600}>
			<div className="rooms-container">
				<div className="flex flex-col gap-20">
					<div className="flex justify-between items-end">
						<span className="text-pd-2xl self-end font-bold mr-28">Rooms</span>
						<div className="flex items-center gap-8 flex-wrap justify-end">
							<TextField
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												id="search-icon"
												onClick={() => {
													document.getElementById('search-input').focus();
												}}
											>
												<img alt="search-icon" height="80%" src="assets/images/search-icon.svg" width="80%" />
											</IconButton>
										</InputAdornment>
									),
								}}
								id="search-input"
								label="Search By Name"
								onChange={handleSearch}
								style={{
									alignSelf: 'center',
									paddingRight: 30,
								}}
								value={searchQuery}
							/>
							<div
								className="flex items-center gap-8 flex-wrap justify-end"
								style={{
									alignSelf: 'center',
									marginTop: 20,
								}}
							>
								<GradientButton onClick={handleGoToUploadSchedules} size="small" variant="tertiary">
									<UploadIcon className="size-16" /> Upload Schedules
								</GradientButton>
								<GradientButton onClick={handleOpenAddRoomPopover} size="small" variant="tertiary">
									Add Room
								</GradientButton>
							</div>
						</div>
					</div>
					<AllRoomsRatioStats loading={ratiosLoading} ratioData={ratioData} />
				</div>
				<TableContainer className="mt-20 p-[24px] pt-0 mb-[162px] h-[60vh]" component={Paper} id="Scrollable-table">
					<Table className="rooms-table" stickyHeader style={{ width: '100%' }}>
						<TableHead>
							<TableRow>
								<TableCell className="bg-white font-bold whitespace-nowrap">Room</TableCell>
								<TableCell className="bg-white font-bold whitespace-nowrap">Students Assigned</TableCell>
								<TableCell className="bg-white font-bold whitespace-nowrap">
									Checked In &nbsp; : &nbsp; Staff In
								</TableCell>
								<TableCell className="bg-white font-bold whitespace-nowrap">Ratio Status</TableCell>
							</TableRow>
						</TableHead>
						<TableBody className="">
							{isLoading ? (
								<TableRow>
									<TableCell align="center" colSpan={8}>
										<CircularProgress size={35} />
									</TableCell>
								</TableRow>
							) : !rooms.length && !firstLoad ? (
								<TableRow>
									<TableCell align="center" colSpan={8}>
										No Rooms
									</TableCell>
								</TableRow>
							) : (
								rooms?.map((room) => {
									const ratio = calculateRatio(room);
									const hasRatioSet = room.student_ratio_count != null;
									const requiredStaff = Math.ceil((room.ratios.student_count / ratio.students) * ratio.staff);
									const isOutOfRatio = room.room_type === 'room' && room.ratios.staff_count < requiredStaff;
									const isUnoccupied = room.ratios.student_count === 0;

									return (
										<TableRow
											id={`room-${room.id}`}
											key={room.id}
											onClick={() => handleGoToRoom(room)}
											style={{ cursor: 'pointer' }}
										>
											<TableCell component="th" scope="row">
												<div className="flex items-center">
													<Avatar className="mr-12 size-[45px]" src={room.thumb} />
													<div className=" max-w-[20ch] truncate font-bold">{room.name}</div>
												</div>
											</TableCell>
											<TableCell>
												<div className="flex items-center" style={{ gap: 5 }}>
													<div className="flex">
														{room.students.length > 0 ? (
															room.students.map((student, index) => {
																const isFirst = index === 0;
																return index < 4 ? (
																	<Avatar
																		className={`size-[30px]  border-2 border-white ${isFirst ? '' : '-ml-16'}`}
																		key={index}
																		src={student?.thumb || student?.photo}
																		style={{ marginRight: 5 }}
																	/>
																) : (
																	''
																);
															})
														) : (
															<span className="text-grey">No students</span>
														)}
													</div>
													<div className="justify-between -ml-10">
														&nbsp;
														{room.students_count > 4 ? `+ ${room.students_count - 4} others` : null}
													</div>
												</div>
											</TableCell>
											{/* Room type "place" == location and won't have a ratio */}
											{room.room_type !== 'place' ? (
												<TableCell className="text-center">
													{hasRatioSet ? (
														<span className="font-bold">
															{room.ratios.student_count} &nbsp; : &nbsp; {room.ratios.staff_count}
														</span>
													) : (
														<span className="font-bold">
															{room.ratios.student_count} &nbsp; : &nbsp; {room.ratios.staff_count}
														</span>
													)}
												</TableCell>
											) : (
												<TableCell className="text-center">
													<span className="font-bold">
														{room.ratios.student_count} &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
													</span>
												</TableCell>
											)}
											<TableCell>
												{room.room_type === 'room' ? (
													isUnoccupied ? (
														<span className="ml-16 text-grey">unoccupied</span>
													) : (
														<span className="font-bold ml-16">{isOutOfRatio ? 'Out of ratio' : 'In ratio'}</span>
													)
												) : (
													<span className="ml-16 text-grey"></span>
												)}
											</TableCell>
										</TableRow>
									);
								})
							)}
							{fetchingMore ? (
								<TableRow>
									<TableCell align="center" colSpan={8}>
										<CircularProgress size={35} />
									</TableCell>
								</TableRow>
							) : null}
						</TableBody>
					</Table>
				</TableContainer>
				<InfiniteScroll
					dataLength={rooms.length}
					hasMore={hasMore}
					next={handleLoadMore}
					scrollableTarget="Scrollable-table"
				/>
				<Popover
					anchorEl={anchorEl}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'center',
					}}
					onClose={handleClose}
					open={Boolean(anchorEl)}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'center',
					}}
				>
					<div className="py-8">
						<MenuItem
							onClick={() => {
								navigate('/rooms-addroom', { state: rooms[0] });
								handleClose();
							}}
						>
							Room
						</MenuItem>
						<MenuItem
							onClick={() => {
								navigate('/location-add', { state: rooms[0] });
								handleClose();
							}}
						>
							Location
						</MenuItem>
					</div>
				</Popover>
			</div>
		</FuseAnimate>
	);
};

export default AllRooms;
